import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { gewerkeApi } from "@/services/api";
import type { GewerkPhase } from "@/types";
import {
  ArrowLeft, Plus, Pencil, Trash2, ChevronUp, ChevronDown,
  Loader2, AlertCircle, CheckCircle2, ListChecks, Save, X,
} from "lucide-react";

// IDs der Objekte, deren Inline-Rename gerade offen ist
interface EditingState {
  type: 'phase' | 'gewerk';
  id: number;
  name: string;
}

/**
 * Verwaltungsseite des globalen Gewerk-Katalogs (Route /bauvorhaben/gewerke,
 * approver+admin). Phasen (Bauablauf-Abschnitte) und ihre Gewerke:
 * anlegen, umbenennen, sortieren (Up/Down), deaktivieren, löschen.
 */
export function GewerkeAdminPage() {
  const [phases, setPhases] = useState<GewerkPhase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isMutating, setIsMutating] = useState(false);

  const [newPhaseName, setNewPhaseName] = useState('');
  const [newGewerkNames, setNewGewerkNames] = useState<Record<number, string>>({});
  const [editing, setEditing] = useState<EditingState | null>(null);

  const loadCatalog = useCallback(() => {
    setIsLoading(true);
    gewerkeApi
      .getAll()
      .then((response) => {
        setPhases(response.data.data.phases);
        setError('');
      })
      .catch(() => setError('Fehler beim Laden des Gewerk-Katalogs'))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const runMutation = async (action: () => Promise<{ message?: string } | void>, fallbackError: string) => {
    setError('');
    setInfo('');
    setIsMutating(true);
    try {
      const result = await action();
      setInfo(((result as { message?: string })?.message) || '');
      loadCatalog();
    } catch (err) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || fallbackError);
    } finally {
      setIsMutating(false);
    }
  };

  const handleAddPhase = () => {
    const name = newPhaseName.trim();
    if (!name) return;
    runMutation(
      async () => {
        const result = await gewerkeApi.createPhase({ name });
        setNewPhaseName('');
        return result.data;
      },
      'Fehler beim Anlegen der Phase'
    );
  };

  const handleAddGewerk = (phaseId: number) => {
    const name = (newGewerkNames[phaseId] ?? '').trim();
    if (!name) return;
    runMutation(
      async () => {
        const result = await gewerkeApi.create({ name, phase_id: phaseId });
        setNewGewerkNames((prev) => ({ ...prev, [phaseId]: '' }));
        return result.data;
      },
      'Fehler beim Anlegen des Gewerks'
    );
  };

  const handleSaveRename = () => {
    if (!editing) return;
    const { type, id, name } = editing;
    if (!name.trim()) {
      setError('Der Name darf nicht leer sein');
      return;
    }
    runMutation(
      async () => {
        const result = type === 'phase'
          ? await gewerkeApi.updatePhase(id, { name: name.trim() })
          : await gewerkeApi.update(id, { name: name.trim() });
        setEditing(null);
        return result.data;
      },
      'Fehler beim Umbenennen'
    );
  };

  const handleMove = (type: 'phase' | 'gewerk', id: number, direction: 'up' | 'down') => {
    if (type === 'phase') {
      const index = phases.findIndex((p) => p.id === id);
      const neighbor = direction === 'up' ? phases[index - 1] : phases[index + 1];
      if (!neighbor) return;
      const current = phases[index];
      runMutation(
        async () => {
          await gewerkeApi.updatePhase(current.id, { sort_order: neighbor.sort_order });
          await gewerkeApi.updatePhase(neighbor.id, { sort_order: current.sort_order });
        },
        'Fehler beim Sortieren'
      );
    } else {
      for (const phase of phases) {
        const index = phase.gewerke.findIndex((g) => g.id === id);
        if (index === -1) continue;
        const neighbor = direction === 'up' ? phase.gewerke[index - 1] : phase.gewerke[index + 1];
        if (!neighbor) return;
        const current = phase.gewerke[index];
        runMutation(
          async () => {
            await gewerkeApi.update(current.id, { sort_order: neighbor.sort_order });
            await gewerkeApi.update(neighbor.id, { sort_order: current.sort_order });
          },
          'Fehler beim Sortieren'
        );
        return;
      }
    }
  };

  const handleToggleActive = (gewerkId: number, isActive: boolean) => {
    runMutation(
      async () => {
        const result = await gewerkeApi.update(gewerkId, { is_active: !isActive });
        return result.data;
      },
      'Fehler beim Umschalten'
    );
  };

  const handleDeleteGewerk = (gewerkId: number, name: string) => {
    if (!window.confirm(`Gewerk "${name}" wirklich löschen?`)) return;
    runMutation(
      async () => {
        const result = await gewerkeApi.remove(gewerkId);
        return result.data;
      },
      'Fehler beim Löschen des Gewerks'
    );
  };

  const handleDeletePhase = (phaseId: number, name: string) => {
    if (!window.confirm(`Phase "${name}" wirklich löschen?`)) return;
    runMutation(
      async () => {
        const result = await gewerkeApi.removePhase(phaseId);
        return result.data;
      },
      'Fehler beim Löschen der Phase'
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-6">
        <Link to="/bauvorhaben" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft className="h-4 w-4" />
          Bauvorhaben
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ListChecks className="h-6 w-6 text-gray-400" />
            Gewerke verwalten
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Diese Phasen gehören zum Bauablauf und ordnen die Gewerke. Jedes Bauvorhaben zeigt den gesamten aktiven Katalog als Checkliste.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newPhaseName}
            onChange={(e) => setNewPhaseName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddPhase()}
            placeholder="Phase hinzufügen…"
            className="input"
          />
          <button
            type="button"
            onClick={handleAddPhase}
            disabled={isMutating || !newPhaseName.trim()}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Phase hinzufügen
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}
      {info && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {info}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
        </div>
      ) : phases.length === 0 ? (
        <div className="card p-8 text-center text-gray-500">
          <ListChecks className="mx-auto mb-3 h-8 w-8 text-gray-400" />
          <p>Noch keine Phasen angelegt</p>
        </div>
      ) : (
        <div className="space-y-4">
          {phases.map((phase, phaseIndex) => (
            <div key={phase.id} className="card p-6">
              <div className="flex items-center justify-between gap-2 mb-4">
                {editing?.type === 'phase' && editing.id === phase.id ? (
                  <div className="flex flex-1 items-center gap-2">
                    <input
                      type="text"
                      value={editing.name}
                      onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveRename()}
                      className="input"
                      autoFocus
                    />
                    <button type="button" onClick={handleSaveRename} disabled={isMutating} className="btn btn-primary p-2" title="Speichern">
                      <Save className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => setEditing(null)} className="btn btn-secondary p-2" title="Abbrechen">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-lg font-semibold text-gray-900">{phase.name}</h2>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleMove('phase', phase.id, 'up')}
                        disabled={phaseIndex === 0}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
                        title="Nach oben"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMove('phase', phase.id, 'down')}
                        disabled={phaseIndex === phases.length - 1}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
                        title="Nach unten"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing({ type: 'phase', id: phase.id, name: phase.name })}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                        title="Phase umbenennen"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeletePhase(phase.id, phase.name)}
                        className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                        title="Phase löschen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>

              {phase.gewerke.length === 0 ? (
                <p className="mb-4 text-sm text-gray-500">Keine Gewerke in dieser Phase.</p>
              ) : (
                <ul className="mb-4 divide-y divide-gray-100">
                  {phase.gewerke.map((gewerk, gewerkIndex) => (
                    <li key={gewerk.id} className="flex items-center justify-between gap-2 py-2">
                      {editing?.type === 'gewerk' && editing.id === gewerk.id ? (
                        <>
                          <input
                            type="text"
                            value={editing.name}
                            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                            onKeyDown={(e) => e.key === 'Enter' && handleSaveRename()}
                            className="input"
                            autoFocus
                          />
                          <button type="button" onClick={handleSaveRename} disabled={isMutating} className="btn btn-primary p-2" title="Speichern">
                            <Save className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => setEditing(null)} className="btn btn-secondary p-2" title="Abbrechen">
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className={`text-sm ${gewerk.is_active ? 'text-gray-900' : 'text-gray-400'}`}>
                            {gewerk.name}
                            {!gewerk.is_active && (
                              <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">inaktiv</span>
                            )}
                          </span>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleMove('gewerk', gewerk.id, 'up')}
                              disabled={gewerkIndex === 0}
                              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
                              title="Nach oben"
                            >
                              <ChevronUp className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMove('gewerk', gewerk.id, 'down')}
                              disabled={gewerkIndex === phase.gewerke.length - 1}
                              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
                              title="Nach unten"
                            >
                              <ChevronDown className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditing({ type: 'gewerk', id: gewerk.id, name: gewerk.name })}
                              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                              title="Gewerk umbenennen"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleActive(gewerk.id, gewerk.is_active)}
                              className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                              title={gewerk.is_active ? 'Aus Checklisten ausblenden' : 'In Checklisten einblenden'}
                            >
                              {gewerk.is_active ? 'Deaktivieren' : 'Aktivieren'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteGewerk(gewerk.id, gewerk.name)}
                              className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                              title="Gewerk löschen"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newGewerkNames[phase.id] ?? ''}
                  onChange={(e) => setNewGewerkNames((prev) => ({ ...prev, [phase.id]: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddGewerk(phase.id)}
                  placeholder="Gewerk hinzufügen…"
                  className="input"
                />
                <button
                  type="button"
                  onClick={() => handleAddGewerk(phase.id)}
                  disabled={isMutating || !(newGewerkNames[phase.id] ?? '').trim()}
                  className="btn btn-secondary inline-flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Hinzufügen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}